/**
 * استعادة سلّة الحساب على أيّ جهاز.
 * المبدأ: السلّة المحليّة هي المصدر الأوّل؛ لا نمسح ولا ندمج بلا إذن العميل.
 * - جهاز فارغ + حساب فيه سلّة → استعادة صامتة.
 * - جهاز فيه سلّة مختلفة → نرفع حالة تفتح حوار الخيار.
 */
import * as React from "react";
import { useLocation } from "@tanstack/react-router";
import { useCartStore } from "@/stores/cartStore";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { getMyCartSnapshot } from "@/lib/cartSnapshot.functions";
import { getMyRecoveryEpisode } from "@/lib/cartRecovery.functions";
import { fetchProductByHandle } from "@/lib/shopify";
import { isAccountRecoveryDestination, selectRecoveryVariant, recoveryEpisodeId } from "@/lib/cartRecoveryRestore";
import { getAuthSnapshot } from "@/lib/auth-snapshot";

export interface RestorableItem {
  handle: string;
  variantId: string;
  quantity: number;
  title: string | null;
  image: string | null;
}

const CHECK_FLAG_PREFIX = "lecmo-cart-restore-v1:";

function alreadyChecked(userId: string): boolean {
  if (typeof window === "undefined") return true;
  try {
    return sessionStorage.getItem(CHECK_FLAG_PREFIX + userId) === "1";
  } catch {
    return true;
  }
}

function markChecked(userId: string) {
  try {
    sessionStorage.setItem(CHECK_FLAG_PREFIX + userId, "1");
  } catch {
    /* ignore */
  }
}

export function useAccountCartRestore() {
  const { user } = useAuth();
  const { effectiveCountryCode } = useLocale();
  const { pathname, searchStr } = useLocation();
  const recovery = pathname === "/cart" && isAccountRecoveryDestination(searchStr);
  const episodeId = recovery ? recoveryEpisodeId(searchStr) : null;
  // Only recovery destinations are navigation-bound; ordinary restore survives page changes.
  const scope = recovery ? `${pathname}?${searchStr}` : "account";
  const navigation = React.useRef({ owner: user?.id, scope });
  navigation.current = { owner: user?.id, scope };
  const countryRef = React.useRef(effectiveCountryCode);
  countryRef.current = effectiveCountryCode;
  const addItem = useCartStore((s) => s.addItem);
  const clearCart = useCartStore((s) => s.clearCart);

  const [pending, setPending] = React.useState<RestorableItem[] | null>(null);
  const [isRestoring, setIsRestoring] = React.useState(false);
  const requestGeneration = React.useRef(0);
  const pendingContext = React.useRef<{ owner: string; recovery: boolean; episodeId: string | null } | null>(null);

  /** يعيد بناء الأسطر في السلّة (وسلّة Shopify) من اللقطة. */
  const applyRestore = React.useCallback(
    async (list: RestorableItem[], replaceLocal: boolean, request = requestGeneration.current) => {
      const owner = user?.id;
      const current = () => requestGeneration.current === request && navigation.current.owner === owner &&
        navigation.current.scope === scope &&
        (!recovery || getAuthSnapshot().userId === owner);
      if (!current() || (recovery && !owner)) return;
      setIsRestoring(true);
      try {
        if (recovery) {
          if (!episodeId) return;
          const episode = await getMyRecoveryEpisode({ data: { episodeId } });
          if (!current() || !episode.ok || !episode.episode) return;
          const country = episode.episode.country;
          const exactList = episode.episode.items;
          // Resolve exact available variants and current prices BEFORE touching local contents.
          const resolved = [];
          for (const entry of exactList) {
            const node = await fetchProductByHandle(entry.handle, country, { cacheTtlMs: 0 });
            if (!current()) return;
            if (!node) continue;
            const variant = selectRecoveryVariant((node.variants?.edges ?? []).map((edge) => edge.node), entry.variantId);
            if (variant) resolved.push({ entry, node, variant });
          }
          if (!current()) return;
          if (!replaceLocal && useCartStore.getState().items.length > 0) {
            if (owner) pendingContext.current = { owner, recovery: true, episodeId };
            setPending(exactList); // Local activity during fetch requires the existing confirmation.
            return;
          }
          if (resolved.length === 0) return;
          const fresh = await getMyRecoveryEpisode({ data: { episodeId } });
          if (!current() || !fresh.ok) return;
          const signature = () => JSON.stringify(useCartStore.getState().items.map((line) => [line.variantId, line.quantity, line.lineId]));
          let expectedSignature = signature();
          let prepared = false;
          for (const { entry, node, variant } of resolved) {
            if (!current()) return;
            const restoreItem = { product: { node }, variantId: variant.id, variantTitle: variant.title,
              price: variant.price, quantity: Math.max(1, entry.quantity),
              selectedOptions: variant.selectedOptions ?? [] };
            const added = await addItem(restoreItem, country, false, {
                isCurrent: current,
                validate: async () => {
                  const latest = await getMyRecoveryEpisode({ data: { episodeId } });
                  if (!current() || !latest.ok) return false;
                  const product = await fetchProductByHandle(entry.handle, country, { cacheTtlMs: 0 });
                  if (!current() || !product) return false;
                  const exactVariant = selectRecoveryVariant(product.variants.edges.map((edge) => edge.node), entry.variantId);
                  if (!exactVariant) return false;
                  restoreItem.product = { node: product }; restoreItem.price = exactVariant.price;
                  return true;
                },
                prepare: () => {
                  // Runs inside addItem's cart lock after authenticated episode/catalog revalidation.
                  if (!current() || signature() !== expectedSignature) {
                    if (current() && owner) {
                      pendingContext.current = { owner, recovery: true, episodeId }; setPending(exactList);
                    }
                    return false;
                  }
                  if (!prepared) {
                    if (replaceLocal) clearCart(null); // Explicit confirmation only; no recovery activity.
                    else if (useCartStore.getState().items.length > 0) return false;
                    prepared = true;
                  }
                  return true;
                },
              });
            if (!current()) return;
            if (!added) return;
            expectedSignature = signature();
          }
          setPending(null); pendingContext.current = null;
          return;
        }
        if (replaceLocal) clearCart();
        for (const entry of list) {
          const node = await fetchProductByHandle(entry.handle, countryRef.current);
          if (!node) continue;
          if (!current()) return;
          const variant =
            node.variants?.edges?.find((e) => e.node.id === entry.variantId)?.node ??
            node.variants?.edges?.[0]?.node;
          if (!variant || variant.availableForSale === false) continue;

          await addItem(
            {
              product: { node },
              variantId: variant.id,
              variantTitle: variant.title,
              price: variant.price,
              quantity: Math.max(1, entry.quantity),
              selectedOptions: variant.selectedOptions ?? [],
            },
            countryRef.current,
            false, // Restoration is not new recovery activity; never enroll historical snapshots.
          );
        }
      } finally {
        if (current()) {
          setIsRestoring(false);
          if (!recovery) setPending(null);
        }
      }
    },
    [addItem, clearCart, user?.id, recovery, episodeId, scope],
  );

  React.useEffect(() => {
    const request = ++requestGeneration.current;
    setPending(null); pendingContext.current = null; setIsRestoring(false);
    if (!user) return;
    if (!recovery && alreadyChecked(user.id)) return;

    let cancelled = false;

    void (async () => {
      try {
        if (recovery && !episodeId) return;
        const res = recovery && episodeId ? await getMyRecoveryEpisode({ data: { episodeId } }) : await getMyCartSnapshot();
        if (cancelled || request !== requestGeneration.current ||
          (recovery && getAuthSnapshot().userId !== user.id)) return;
        markChecked(user.id);

        const rawItems = "episode" in res ? res.episode?.items ?? [] : res.snapshot?.items ?? [];
        const items = rawItems
          .filter((i) => !("isGiftSample" in i && i.isGiftSample) && i.quantity > 0 && i.handle && i.variantId)
          .map<RestorableItem>((i) => ({
            handle: String(i.handle),
            variantId: String(i.variantId),
            quantity: i.quantity,
            title: i.title ?? null,
            image: i.image ?? null,
          }));

        if (items.length === 0) return;

        const localItems = useCartStore.getState().items;
        const sameCart =
          localItems.length > 0 &&
          items.every((i) =>
            localItems.some((l) => l.variantId === i.variantId && l.quantity >= i.quantity),
          );
        if (sameCart) return;

        if (localItems.length === 0) {
          await applyRestore(items, false, request);
        } else {
          pendingContext.current = { owner: user.id, recovery, episodeId };
          setPending(items);
        }
      } catch (err) {
        console.error("[useAccountCartRestore]", err);
      }
    })();

    return () => {
      cancelled = true;
      if (requestGeneration.current === request) requestGeneration.current++;
    };
  }, [user?.id, applyRestore, scope]);

  const restore = React.useCallback(() => {
    const context = pendingContext.current;
    if (pending && context?.owner === user?.id && context?.recovery === recovery && context?.episodeId === episodeId) void applyRestore(pending, true);
  }, [pending, applyRestore, user?.id, recovery, episodeId]);

  const dismiss = React.useCallback(() => { setPending(null); pendingContext.current = null; }, []);

  return { pending, isRestoring, restore, dismiss };
}
